import { useState } from "react";
import { useQuery } from "@tanstack/react-query";
import {
  LineChart, Line, BarChart, Bar, XAxis, YAxis, Tooltip, ResponsiveContainer, CartesianGrid, Cell,
} from "recharts";
import { Loader2, DollarSign, Users, CalendarCheck, Star, AlertTriangle } from "lucide-react";
import api from "@/lib/api";
import { money } from "@/lib/format";

const COLORS = ["#144F75", "#D98E73", "#5C8A72", "#E0A458", "#8FA89B", "#B5794E"];

function Card({ children, className = "" }) {
  return <div className={`eyw-card p-6 ${className}`}>{children}</div>;
}

function NsList({ title, items, nameKey }) {
  const max = Math.max(1, ...(items || []).map((i) => i.count || i.no_shows || 0));
  return (
    <div>
      <p className="text-xs uppercase tracking-wide text-[#8FA89B] mb-2">{title}</p>
      {(!items || items.length === 0) ? <p className="text-sm text-[#8FA89B]">—</p> : (
        <div className="space-y-1.5">
          {items.map((i, idx) => {
            const v = i.count ?? i.no_shows ?? 0;
            return (
              <div key={idx}>
                <div className="flex justify-between text-sm"><span className="text-[#4A5D6B] truncate pr-2">{i[nameKey]}</span><span className="font-medium text-[#1A242B]">{v}</span></div>
                <div className="h-1.5 bg-[#F1EEE8] rounded-full mt-0.5"><div className="h-1.5 bg-[#B5794E] rounded-full" style={{ width: `${(v / max) * 100}%` }} /></div>
              </div>
            );
          })}
        </div>
      )}
    </div>
  );
}


export default function AnalyticsPanel({ onDrillAppointments }) {
  const [days, setDays] = useState(30);
  const { data, isLoading } = useQuery({
    queryKey: ["analytics", days],
    queryFn: () => api.get(`/admin/analytics?days=${days}`).then((r) => r.data),
  });
  const { data: lowStock } = useQuery({ queryKey: ["low-stock"], queryFn: () => api.get("/admin/low-stock").then((r) => r.data) });
  const { data: apptCounts } = useQuery({ queryKey: ["appt-counts"], queryFn: () => api.get("/admin/appointments?preset=today").then((r) => r.data.counts) });
  const { data: noShow } = useQuery({ queryKey: ["no-show-insights"], queryFn: () => api.get("/admin/no-show-insights?days=90").then((r) => r.data) });

  if (isLoading || !data) return <div className="grid place-items-center py-20 text-[#4A5D6B]"><Loader2 className="animate-spin" /></div>;

  const roleData = Object.entries(data.members.by_role).map(([role, count]) => ({ role, count }));
  const kindData = Object.entries(data.revenue.by_kind).map(([kind, amount]) => ({ kind, amount }));

  return (
    <div data-testid="analytics-panel">
      <div className="flex items-center justify-between mb-6 flex-wrap gap-3">
        <h2 className="font-serif text-2xl text-[#1A242B]">Ecosystem analytics</h2>
        <select value={days} onChange={(e) => setDays(Number(e.target.value))} data-testid="analytics-range" className="eyw-input !py-2 text-sm">
          <option value={7}>Last 7 days</option>
          <option value={30}>Last 30 days</option>
          <option value={90}>Last 90 days</option>
          <option value={365}>Last year</option>
        </select>
      </div>

      {lowStock?.count > 0 && (
        <div className="mb-6 rounded-xl bg-[#D98E73]/12 border border-[#D98E73]/40 px-5 py-3 flex items-center gap-3" data-testid="low-stock-banner">
          <AlertTriangle size={18} className="text-[#B5794E]" />
          <span className="text-sm text-[#1A242B]"><b>{lowStock.count}</b> product{lowStock.count > 1 ? "s" : ""} at or below reorder threshold: {lowStock.items.slice(0, 4).map((i) => i.title).join(", ")}{lowStock.count > 4 ? "…" : ""}</span>
        </div>
      )}

      <div className="grid grid-cols-2 lg:grid-cols-4 gap-4 mb-8">
        <Card><div className="flex items-center justify-between"><span className="text-3xl font-serif text-[#144F75]">{money(data.revenue.total)}</span><DollarSign size={20} className="text-[#D98E73]" /></div><p className="text-sm text-[#4A5D6B] mt-1">Revenue ({days}d)</p></Card>
        <Card><div className="flex items-center justify-between"><span className="text-3xl font-serif text-[#144F75]">{data.members.total}</span><Users size={20} className="text-[#D98E73]" /></div><p className="text-sm text-[#4A5D6B] mt-1">Total members</p></Card>
        <Card><div className="flex items-center justify-between"><span className="text-3xl font-serif text-[#144F75]">{data.bookings.total}</span><CalendarCheck size={20} className="text-[#D98E73]" /></div><p className="text-sm text-[#4A5D6B] mt-1">Bookings ({days}d)</p></Card>
        <Card><div className="flex items-center justify-between"><span className="text-3xl font-serif text-[#144F75]">{data.members.signups.reduce((s, x) => s + x.value, 0)}</span><Users size={20} className="text-[#D98E73]" /></div><p className="text-sm text-[#4A5D6B] mt-1">New signups ({days}d)</p></Card>
      </div>

      {onDrillAppointments && apptCounts && (
        <div className="mb-8" data-testid="analytics-drilldowns">
          <p className="text-xs uppercase tracking-wide text-[#8FA89B] mb-2">Operations — click to open the records</p>
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-4">
            {[
              { preset: "today", label: "Today's appointments", value: apptCounts.today, color: "#144F75" },
              { preset: "pending", label: "Pending approval", value: apptCounts.pending, color: "#B5794E" },
              { preset: "outstanding", label: "Outstanding balances", value: apptCounts.outstanding, color: "#B5794E" },
              { preset: "no_show", label: "No-shows", value: apptCounts.no_show, color: "#B45151" },
            ].map((c) => (
              <button key={c.preset} onClick={() => onDrillAppointments(c.preset)} data-testid={`drill-${c.preset}`}
                className="eyw-card p-5 text-left hover:shadow-md transition-shadow cursor-pointer">
                <span className="text-3xl font-serif" style={{ color: c.color }}>{c.value ?? 0}</span>
                <p className="text-sm text-[#4A5D6B] mt-1">{c.label} →</p>
              </button>
            ))}
          </div>
        </div>
      )}

      {noShow && noShow.considered > 0 && (
        <div className="eyw-card p-5 mb-8" data-testid="no-show-insights">
          <div className="flex items-center justify-between flex-wrap gap-2 mb-3">
            <h3 className="font-serif text-lg text-[#1A242B]">No-show insights <span className="text-sm text-[#8FA89B] font-sans">(last 90 days)</span></h3>
            <span className="text-sm text-[#4A5D6B]"><b style={{ color: noShow.rate_pct > 10 ? "#B45151" : "#5C8A72" }}>{noShow.rate_pct}%</b> no-show rate · {noShow.total_no_shows} of {noShow.considered}</span>
          </div>
          {noShow.total_no_shows === 0 ? <p className="text-sm text-[#5C8A72]">No no-shows in this period — great retention!</p> : (
            <div className="grid md:grid-cols-3 gap-4">
              <NsList title="Most-missed services" items={noShow.by_service} nameKey="service" />
              <NsList title="Busiest no-show times" items={noShow.by_hour} nameKey="label" />
              <NsList title="By weekday" items={noShow.by_weekday} nameKey="label" />
            </div>
          )}
        </div>
      )}


      <div className="grid lg:grid-cols-2 gap-6 mb-6">
        <Card>
          <h3 className="font-serif text-lg text-[#1A242B] mb-4">Revenue over time</h3>
          {data.revenue.series.length === 0 ? <p className="text-sm text-[#8FA89B] py-12 text-center">No paid revenue in this window.</p> : (
            <ResponsiveContainer width="100%" height={240}>
              <LineChart data={data.revenue.series}>
                <CartesianGrid strokeDasharray="3 3" stroke="#EDE8E0" />
                <XAxis dataKey="date" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} />
                <Tooltip /><Line type="monotone" dataKey="value" stroke="#144F75" strokeWidth={2} dot={false} />
              </LineChart>
            </ResponsiveContainer>
          )}
        </Card>
        <Card>
          <h3 className="font-serif text-lg text-[#1A242B] mb-4">Membership by tier</h3>
          <ResponsiveContainer width="100%" height={240}>
            <BarChart data={roleData}>
              <CartesianGrid strokeDasharray="3 3" stroke="#EDE8E0" />
              <XAxis dataKey="role" tick={{ fontSize: 11 }} /><YAxis tick={{ fontSize: 11 }} allowDecimals={false} />
              <Tooltip /><Bar dataKey="count" radius={[6, 6, 0, 0]}>{roleData.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}</Bar>
            </BarChart>
          </ResponsiveContainer>
        </Card>
      </div>

      <div className="grid lg:grid-cols-2 gap-6">
        <Card>
          <h3 className="font-serif text-lg text-[#1A242B] mb-4">Revenue by source</h3>
          {kindData.length === 0 ? <p className="text-sm text-[#8FA89B] py-8 text-center">No revenue yet.</p> : (
            <div className="space-y-2">
              {kindData.map((k) => (
                <div key={k.kind} className="flex items-center justify-between text-sm">
                  <span className="capitalize text-[#4A5D6B]">{k.kind.replace("_", " ")}</span>
                  <span className="font-semibold text-[#144F75]">{money(k.amount)}</span>
                </div>
              ))}
            </div>
          )}
          <h3 className="font-serif text-lg text-[#1A242B] mt-8 mb-3">Event fill rates</h3>
          <div className="space-y-3">
            {data.event_fill.slice(0, 6).map((e, i) => {
              const pct = e.capacity > 0 ? Math.min(100, Math.round((e.registered / e.capacity) * 100)) : 0;
              return (
                <div key={i} data-testid={`event-fill-${i}`}>
                  <div className="flex justify-between text-xs text-[#4A5D6B] mb-1"><span className="truncate">{e.title}</span><span>{e.registered}/{e.capacity || "∞"}</span></div>
                  <div className="h-2 rounded-full bg-[#EDE8E0]"><div className="h-2 rounded-full bg-[#144F75]" style={{ width: `${pct}%` }} /></div>
                </div>
              );
            })}
            {data.event_fill.length === 0 && <p className="text-sm text-[#8FA89B]">No events.</p>}
          </div>
        </Card>
        <Card>
          <h3 className="font-serif text-lg text-[#1A242B] mb-4">Top Enhancers</h3>
          {data.top_enhancers.length === 0 ? <p className="text-sm text-[#8FA89B] py-8 text-center">No enhancer revenue yet.</p> : (
            <div className="divide-y divide-[#E5E0D8]">
              {data.top_enhancers.map((e, i) => (
                <div key={e.id} className="flex items-center justify-between py-3" data-testid={`top-enhancer-${i}`}>
                  <div className="flex items-center gap-2">
                    <span className="grid place-items-center h-7 w-7 rounded-full bg-[#144F75]/10 text-[#144F75] text-xs font-semibold">{i + 1}</span>
                    <span className="text-sm font-semibold text-[#1A242B]">{e.name}</span>
                    <span className="text-xs text-[#8FA89B] flex items-center gap-0.5"><Star size={11} className="fill-[#E0A458] text-[#E0A458]" /> {e.rating || "—"}</span>
                  </div>
                  <span className="font-serif text-[#144F75]">{money(e.revenue)}</span>
                </div>
              ))}
            </div>
          )}
        </Card>
      </div>
    </div>
  );
}
